'use client';

import { format } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { notFound } from 'next/navigation';
import { useGetMatch } from '@/api/matches/matches';
import type {
  PandaScoreGame,
  PandaScoreMatch,
  PandaScoreOpponentEntity,
} from '@/api/teams/teams.types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { TeamPlaceholderIcon } from '@/components/icons/teams/TeamPlaceholderIcon';
import { MatchOdds } from '@/components/pages/matches/MatchOdds';
import { PageContent } from '@/components/PageContent';
import { PageSectionTitle } from '@/components/PageSectionTitle';
import { Game } from '@/constants/game.const';
import { getGameFromVideogameSlug } from '@/helpers/videogame.helpers';
import { cn } from '@/utils/cn';

const formatDuration = (seconds?: number | null): string | null => {
  if (!seconds || seconds <= 0) {
    return null;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const seriesScore = (match: PandaScoreMatch, teamId?: number): number => {
  if (teamId == null) {
    return 0;
  }
  const fromResults = match.results.find((r) => r.teamId === teamId)?.score;
  if (fromResults != null) {
    return fromResults;
  }
  // Fall back to counting per-game wins.
  return (match.games ?? []).filter(
    (g) => (g.winner?.id ?? g.winnerId) === teamId
  ).length;
};

const TeamBlock = ({
  team,
  align,
}: {
  team: PandaScoreOpponentEntity | null;
  align: 'left' | 'right';
}) => {
  const inner = (
    <div
      className={cn(
        'flex min-w-0 items-center gap-3',
        align === 'right' && 'flex-row-reverse text-right'
      )}
    >
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-card-bg">
        {team?.imageUrl ? (
          <Image
            alt={team.name ?? 'Team'}
            className="h-full w-full object-contain p-1"
            height={56}
            src={team.imageUrl}
            width={56}
          />
        ) : (
          <TeamPlaceholderIcon className="h-7 w-7" desc="" title="" />
        )}
      </div>
      <span className="truncate font-semibold text-lg text-white">
        {team?.name ?? 'TBD'}
      </span>
    </div>
  );

  return team?.slug ? (
    <Link className="min-w-0 flex-1 hover:opacity-80" href={`/teams/${team.slug}`}>
      {inner}
    </Link>
  ) : (
    <div className="min-w-0 flex-1">{inner}</div>
  );
};

const STATUS_STYLE: Record<string, string> = {
  finished: 'bg-white-o5 text-blue-gray',
  running: 'bg-red-o25 text-red',
  not_started: 'bg-light-blue-o15 text-light-blue',
};

const STATUS_LABEL: Record<string, string> = {
  finished: 'Finished',
  running: 'Live',
  not_started: 'Upcoming',
};

const GameRow = ({
  game,
  teamById,
  isCs,
}: {
  game: PandaScoreGame;
  teamById: Map<number, PandaScoreOpponentEntity>;
  isCs: boolean;
}) => {
  const winnerId = game.winner?.id ?? game.winnerId ?? null;
  const winner = winnerId != null ? teamById.get(winnerId) : null;
  const status = game.status ?? 'not_started';
  const duration = formatDuration(game.length);

  // CS games have a map; Dota games don't. Only show "To be decided" for CS
  // games that haven't been picked yet — finished games without a map name
  // fall back to a neutral label rather than implying the map is undecided.
  const mapName =
    game.map?.name ??
    (status === 'finished' ? `Map ${game.position}` : 'To be decided');

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-card-bg p-3">
      <div className="flex min-w-0 items-center gap-3">
        <span className="w-12 shrink-0 font-semibold text-blue-gray text-xs">
          {isCs ? 'MAP' : 'GAME'} {game.position}
        </span>
        {isCs && (
          <div className="flex h-9 w-14 shrink-0 items-center justify-center overflow-hidden rounded bg-light-gray">
            {game.map?.imageUrl ? (
              <Image
                alt={game.map.name ?? 'Map'}
                className="h-full w-full object-cover"
                height={36}
                src={game.map.imageUrl}
                width={56}
              />
            ) : (
              <span className="text-[10px] text-blue-gray">—</span>
            )}
          </div>
        )}
        <span className="truncate font-medium text-sm text-white">
          {isCs ? mapName : duration ? `Duration ${duration}` : `Game ${game.position}`}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {winner ? (
          <span className="truncate text-sm text-green">{winner.name}</span>
        ) : (
          <span className="text-blue-gray text-sm">—</span>
        )}
        <span
          className={cn(
            'rounded px-1.5 py-0.5 font-medium text-[10px]',
            STATUS_STYLE[status] ?? 'bg-white-o5 text-blue-gray'
          )}
        >
          {STATUS_LABEL[status] ?? status}
        </span>
      </div>
    </div>
  );
};

export const MatchDetailPage = ({ id }: { id: string }) => {
  const { data, isLoading, isError } = useGetMatch(id);
  const match = data?.data;

  if (isLoading) {
    return (
      <PageContent>
        <div className="flex animate-pulse flex-col gap-6">
          <div className="h-32 rounded-lg bg-card-bg" />
          <div className="h-40 rounded-lg bg-card-bg" />
        </div>
      </PageContent>
    );
  }

  if (isError || !match) {
    notFound();
  }

  const teamA = match.opponents[0]?.opponent ?? null;
  const teamB = match.opponents[1]?.opponent ?? null;
  const scoreA = seriesScore(match, teamA?.id);
  const scoreB = seriesScore(match, teamB?.id);
  const status = match.status ?? 'not_started';

  const teamById = new Map<number, PandaScoreOpponentEntity>();
  for (const o of match.opponents) {
    if (o.opponent) {
      teamById.set(o.opponent.id, o.opponent);
    }
  }

  const games = [...(match.games ?? [])].sort((a, b) => a.position - b.position);
  const context = [match.league?.name, match.serie?.fullName, match.tournament?.name]
    .filter(Boolean)
    .join(' • ');
  const title = teamA && teamB ? `${teamA.name} vs ${teamB.name}` : 'Match';
  const isCs = getGameFromVideogameSlug(match.videogame?.slug) === Game.CS2;
  const sectionTitle = isCs ? 'Maps' : 'Games';

  return (
    <PageContent>
      <Breadcrumbs
        breadcrumbs={[{ title: 'Matches', href: '/matches' }, { title }]}
        className="mb-6"
      />

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4 rounded-lg bg-custom-gradient p-5">
          {context && (
            <span className="text-blue-gray text-sm">{context}</span>
          )}
          <div className="flex items-center justify-between gap-4">
            <TeamBlock align="left" team={teamA} />
            <div className="flex shrink-0 flex-col items-center">
              <span className="font-bold text-2xl text-white">
                {scoreA} <span className="text-blue-gray">:</span> {scoreB}
              </span>
              <span
                className={cn(
                  'mt-1 rounded px-1.5 py-0.5 font-medium text-[10px]',
                  STATUS_STYLE[status] ?? 'bg-white-o5 text-blue-gray'
                )}
              >
                {STATUS_LABEL[status] ?? status}
              </span>
            </div>
            <TeamBlock align="right" team={teamB} />
          </div>
          {match.beginAt && (
            <span className="text-center text-[11px] text-blue-gray">
              {format(new Date(match.beginAt), 'dd MMM yyyy, HH:mm')}
            </span>
          )}
        </div>

        <section className="flex flex-col gap-3">
          <PageSectionTitle>Odds</PageSectionTitle>
          <MatchOdds match={match} />
        </section>

        <section className="flex flex-col gap-3">
          <PageSectionTitle>{sectionTitle}</PageSectionTitle>
          {games.length === 0 ? (
            <p className="rounded-lg bg-card-bg p-4 text-blue-gray text-sm">
              {isCs
                ? 'The map breakdown isn’t available yet — maps are usually set once the pick/ban happens, closer to match time.'
                : 'The game breakdown isn’t available yet.'}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {games.map((game) => (
                <GameRow
                  game={game}
                  isCs={isCs}
                  key={game.id}
                  teamById={teamById}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </PageContent>
  );
};
